// Ordering and summary helpers for the Health page
import type { ServiceCheck, ServiceStatus } from '../types/system-health';

// Lower comes first: what needs attention sits at the top of the list
const ATTENTION_ORDER: Record<ServiceStatus, number> = {
  down: 0,
  unknown: 1,
  up: 2,
  not_configured: 3,
};

/** Returns a copy with broken services first, keeping the API order within each status */
export const sortByAttention = (services: ServiceCheck[]): ServiceCheck[] =>
  services
    .map((service, index) => ({ service, index }))
    .sort(
      (a, b) =>
        ATTENTION_ORDER[a.service.status] - ATTENTION_ORDER[b.service.status] || a.index - b.index
    )
    .map(({ service }) => service);

export interface HealthSummary {
  level: 'down' | 'unknown' | 'ok';
  count: number;
}

/**
 * Banner summary: down wins, then unconfirmed. "ok" only when nothing is down or
 * unconfirmed, because an unconfirmed service may be broken (e.g. a revoked key).
 */
export const summarize = (services: ServiceCheck[]): HealthSummary => {
  const down = services.filter((s) => s.status === 'down').length;
  if (down > 0) return { level: 'down', count: down };
  const unknown = services.filter((s) => s.status === 'unknown').length;
  if (unknown > 0) return { level: 'unknown', count: unknown };
  return { level: 'ok', count: 0 };
};
